import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { GLASS_SECONDARY_TEXT_CLASS, compressStateChips, itemVisualDensity, overlapCounts, productTone, readNumberMeta, readProjectionCouplingLabel, visibleLaneItems } from "./rendering";

function stateChip(label: string) {
  return <span className="rounded-full border border-fuchsia-300/18 bg-fuchsia-300/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-fuchsia-50/86">{label}</span>;
}

function readProjectionCounts(lane: LaneRendererProps["lane"]) {
  const projectionHealth = lane.metadata?.projectionHealth;
  const byStatus = projectionHealth && typeof projectionHealth === "object" && "byStatus" in projectionHealth && projectionHealth.byStatus && typeof projectionHealth.byStatus === "object"
    ? projectionHealth.byStatus as Record<string, unknown>
    : {};

  const readCount = (key: string) => {
    const value = byStatus[key];
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
  };

  return {
    stale: readCount("stale"),
    invalid: readCount("invalid"),
    reviewRequired: readCount("review_required"),
  };
}

function awaitingLabel(authoredArcSegmentCount: number, items: LaneRendererProps["items"], visibleItemCount: number) {
  if (items.length > 0 && visibleItemCount === 0) return "outside window";
  if (authoredArcSegmentCount > 0) return "awaiting emotion projection";
  return "awaiting arc intent";
}

function emotionGradient(valence: number, intensity: number, urgency: number) {
  const warmth = Math.round(168 + (45 * urgency));
  const cool = Math.round(110 + (55 * (1 - urgency)));
  const lift = Math.max(0, valence);
  const weight = Math.max(0, -valence);
  return `linear-gradient(135deg,
    rgba(${cool}, 211, 252, ${0.16 + (0.18 * intensity)}) 0%,
    rgba(${warmth}, ${92 + Math.round(80 * lift)}, ${245 - Math.round(45 * weight)}, ${0.18 + (0.18 * intensity)}) 55%,
    rgba(251, ${180 - Math.round(40 * (1 - urgency))}, ${85 + Math.round(40 * urgency)}, ${0.18 + (0.2 * urgency)}) 100%)`;
}

export default function ProjectionEmotionLaneRenderer({ lane, items, viewport, syncBadge }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);
  const overlaps = overlapCounts(visibleItems);
  const authoredArcSegmentCount = readNumberMeta({ ...lane, metadata: lane.metadata } as never, "authoredArcSegmentCount") ?? 0;
  const projectionCounts = readProjectionCounts(lane);
  const emptyState = awaitingLabel(authoredArcSegmentCount, items, visibleItems.length);
  const laneStateChips = [
    projectionCounts.stale > 0 ? `${projectionCounts.stale} stale` : null,
    projectionCounts.invalid > 0 ? `${projectionCounts.invalid} invalid` : null,
    projectionCounts.reviewRequired > 0 ? `${projectionCounts.reviewRequired} review` : null,
  ].filter((value): value is string => Boolean(value));
  const primaryCouplingLabel = readProjectionCouplingLabel(lane.metadata ?? null, lane.id, viewport.lodTier);
  const compressedLaneState = compressStateChips(laneStateChips, viewport.lodTier);
  const showBodyReason = viewport.lodTier !== "far";

  return (
    <div className="relative h-full min-h-[48px]" data-lane-product="projection_emotion" data-lane-id={lane.id}>
      {visibleItems.length > 0 ? (
        <>
          {visibleItems.map(({ item, leftPx, widthPx }) => {
          const valence = readNumberMeta(item, "valence") ?? 0;
          const intensity = readNumberMeta(item, "intensity") ?? 0;
          const urgency = readNumberMeta(item, "urgency") ?? 0;
          const label = "label" in item ? item.label : item.id;
          const density = itemVisualDensity(widthPx);
          const overlapCount = overlaps.get(item.id) ?? 1;
          const showNearDetail = viewport.lodTier === "near" && density === "regular";
          const showCompactDetail = viewport.lodTier === "mid" || density === "compact";

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-11 -translate-y-1/2 overflow-hidden rounded-2xl border border-fuchsia-300/22 px-3 shadow-[0_12px_24px_rgba(168,85,247,0.10)]"
              style={{ left: leftPx, width: widthPx, backgroundImage: emotionGradient(valence, intensity, urgency) }}
              title={label}
            >
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.02))]" />
              <div className="relative flex h-full items-center justify-between gap-2">
                <div className="min-w-0">
                  {showNearDetail ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-white">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1 overflow-hidden">
                        <span className="rounded-full border border-white/14 bg-black/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-white/82">V {valence >= 0 ? "+" : "-"}{Math.abs(valence).toFixed(2)}</span>
                        <span className="rounded-full border border-white/14 bg-black/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-white/82">I {intensity.toFixed(2)}</span>
                        <span className="rounded-full border border-white/14 bg-black/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-white/82">U {urgency.toFixed(2)}</span>
                      </div>
                    </>
                  ) : showCompactDetail ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-white">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1 overflow-hidden">
                        <span className="rounded-full border border-white/14 bg-black/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-white/82">I {intensity.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-[8px] font-semibold uppercase tracking-[0.16em] text-white/82">EMO</div>
                  )}
                </div>
                {showNearDetail ? (
                  <div className="flex shrink-0 items-center gap-1.5">
                    {overlapCount > 1 ? (
                      <div className="rounded-full border border-white/16 bg-black/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-white/84">x{overlapCount}</div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex shrink-0 items-center gap-1.5">
                    <div className="h-6 w-6 rounded-full border border-white/16 bg-black/12 text-[8px] font-semibold leading-6 text-center text-white/84">
                      {intensity.toFixed(1)}
                    </div>
                    {overlapCount > 1 ? (
                      <div className="rounded-full border border-white/16 bg-black/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-white/84">x{overlapCount}</div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          );
          })}
        </>
      ) : (
        <div className={["relative min-h-[56px] overflow-hidden rounded-2xl border px-3 py-3", tone.emptyClassName].join(" ")}>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
            <span>
              {items.length > 0
                ? "Emotion projection is outside the active window."
                : authoredArcSegmentCount > 0
                  ? "Emotion projection is waiting on derived ribbons."
                  : "Emotion projection has no authored ribbon yet."}
            </span>
            {stateChip(emptyState)}
            {compressedLaneState.visible.map((label) => (
              <React.Fragment key={label}>{stateChip(label)}</React.Fragment>
            ))}
            {compressedLaneState.hiddenCount > 0 ? stateChip(`+${compressedLaneState.hiddenCount} more`) : null}
            {primaryCouplingLabel ? stateChip(primaryCouplingLabel) : null}
          </div>
          {showBodyReason ? (
          <div className={["mt-1 max-w-[34rem] text-[10px]", GLASS_SECONDARY_TEXT_CLASS].join(" ")}>
            {syncBadge?.reason ?? "Author arc segments and the lane will project emotional vector ranges across the focused timeline window."}
          </div>
          ) : null}
        </div>
      )}
    </div>
  );
}